
import { ReactNode } from "react";
import { Slack, Instagram, Mail, MessageSquare } from "lucide-react";
import SlackIntegration from "../SlackIntegration";
import InstagramIntegration from "../instagram/InstagramIntegration";
import GmailIntegration from "../gmail/GmailIntegration";

/**
 * Interface for integration registry entry
 */
export interface IntegrationRegistryEntry {
  id: string;
  name: string;
  description: string;
  icon: ReactNode;
  component: React.ComponentType<any>;
  category: "communication" | "business" | "analytics";
  isAvailable: boolean;
  requiredPermission?: string;
}

/**
 * Create icon elements with proper styling
 */
const createSlackIcon = (): ReactNode => <Slack className="h-4 w-4" />;
const createInstagramIcon = (): ReactNode => <Instagram className="h-4 w-4 text-pink-500" />;
const createMailIcon = (): ReactNode => <Mail className="h-4 w-4" />;
const createTeamsIcon = (): ReactNode => <MessageSquare className="h-4 w-4" />;

/**
 * Registry of all available integrations
 */
export const availableIntegrations: IntegrationRegistryEntry[] = [
  {
    id: "slack",
    name: "Slack",
    description: "Connect your AI staff to Slack channels",
    icon: createSlackIcon(),
    component: SlackIntegration,
    category: "communication",
    isAvailable: true
  },
  {
    id: "instagram",
    name: "Instagram",
    description: "Create and schedule Instagram posts",
    icon: createInstagramIcon(),
    component: InstagramIntegration,
    category: "business",
    isAvailable: true
  },
  {
    id: "email",
    name: "Email Integration",
    description: "Connect your email for automated campaigns",
    icon: createMailIcon(),
    component: GmailIntegration,
    category: "communication",
    isAvailable: false
  },
  {
    id: "teams",
    name: "Microsoft Teams",
    description: "Connect your AI staff to Microsoft Teams",
    icon: createTeamsIcon(),
    component: () => <div>Coming soon</div>,
    category: "communication",
    isAvailable: false
  }
];

/**
 * Helper function to get integrations by category
 */
export const getIntegrationsByCategory = (category: "communication" | "business" | "analytics") => {
  return availableIntegrations.filter(integration => integration.category === category);
};

/**
 * Helper function to get a specific integration by ID
 */
export const getIntegrationById = (id: string) => {
  return availableIntegrations.find(integration => integration.id === id);
};
